import React from 'react';
import { DateTime } from 'luxon';
import {
	ColumnVisibilityState,
	DATE_SHORTCUTS,
	scanlogsSearchFields,
} from './constants';
import {
	AdapterLuxon,
	BDSProvider,
	DataTable,
	Grid,
	LocalizationProvider,
	SideMenuDropdown,
} from '@barracuda-internal/bds-core-v2';
import { ScanLogEntry, ScanLogsFilter } from 'src/types/scanLogTypes';

interface ScanlogsDataTableProps {
	tableData: ScanLogEntry[] | null;
	page: number;
	pageSize: number;
	isLoading: boolean;
	totalRecords: number;
	dateRange: [DateTime | null, DateTime | null];
	searchValue: string;
	isSearchFocused: boolean;
	columnVisibility: ColumnVisibilityState;
	sorting: Array<{ id: string; desc: boolean }>;
	setSorting: (val: Array<{ id: string; desc: boolean }>) => void;
	filters: ScanLogsFilter[];
	sideMenuDropdowns: SideMenuDropdown[];
	isColumnVisibilityDrawerOpen: boolean;
	setIsColumnVisibilityDrawerOpen: (val: boolean) => void;
	setSideMenuSearchValue: (val: string) => void;
	datePickerError: boolean;
	datePickerErrorMessage: string;
	handleDateRangeChange: (
		dateRange: [DateTime | null, DateTime | null],
	) => void;
	handleSearchFieldChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleRefresh: () => void;
	handlePaginationChange: (e: any, val: number) => void;
	handleOnClickItemsPerPageOption: (val: number) => void;
	handleSideMenuCheckboxChange: (
		e: React.ChangeEvent<HTMLInputElement>,
	) => void;
	handleRemoveColumnFilter: (col: string) => void;
	handleClearAllFilters: () => void;
	downloadFile: () => void;
	columns: any[];
	generateFilterChips: (filters: ScanLogsFilter[], handler: any) => any[];
	classes: any;
	TopBarActions: any;
	SearchEndAdornment: any;
	searchInputProps: any;
	enableFilters?: boolean;
	enableGrouping?: boolean;
	enableColumnDragging?: boolean;
	enablePagination?: boolean;
	enableStickyHeader?: boolean;
	manualSorting?: boolean;
	enableColumnFilterModes?: boolean;
	enableTopToolbar?: boolean;
	enableColumnFilters?: boolean;
	enableGlobalFilter?: boolean;
	showSearchFields?: boolean;
}

export const ScanlogsDataTable: React.FC<ScanlogsDataTableProps> = ({
	tableData,
	page,
	pageSize,
	isLoading,
	totalRecords,
	dateRange,
	searchValue,
	isSearchFocused,
	columnVisibility,
	sorting,
	setSorting,
	filters,
	sideMenuDropdowns,
	isColumnVisibilityDrawerOpen,
	setIsColumnVisibilityDrawerOpen,
	setSideMenuSearchValue,
	datePickerError,
	datePickerErrorMessage,
	handleDateRangeChange,
	handleSearchFieldChange,
	handleRefresh,
	handlePaginationChange,
	handleOnClickItemsPerPageOption,
	handleSideMenuCheckboxChange,
	handleRemoveColumnFilter,
	handleClearAllFilters,
	downloadFile,
	columns,
	generateFilterChips,
	classes,
	TopBarActions,
	SearchEndAdornment,
	searchInputProps,
	enableFilters = true,
	enableGrouping = false,
	enableColumnDragging = false,
	enablePagination = false,
	enableStickyHeader = true,
	manualSorting = true,
	enableColumnFilterModes = false,
	enableTopToolbar = false,
	enableColumnFilters = true,
	enableGlobalFilter = true,
	showSearchFields = true,
}) => {
	return (
		<BDSProvider>
			<Grid container sx={{ width: '100%', overflowX: 'auto' }}>
				<LocalizationProvider dateAdapter={AdapterLuxon}>
					<DataTable
						layoutMode="semantic"
						columns={columns}
						data={tableData}
						dateRangeProps={{
							label: 'Date Range',
							value: dateRange,
							onChange: handleDateRangeChange,
							shortcutsItems: DATE_SHORTCUTS,
							maxDate: DateTime.local(),
							minDate: DateTime.local().minus({
								days: 90,
							}),
							error: datePickerError,
							errorMessage: datePickerErrorMessage,
						}}
						searchFieldProps={{
							value: searchValue,
							onChange: handleSearchFieldChange,
							placeholder: 'Search by File, Owner or Creator',
							autoComplete: 'off',
							InputProps: {
								...searchInputProps,
								endAdornment: (
									<SearchEndAdornment
										searchValue={searchValue}
										isSearchFocused={isSearchFocused}
										scanlogsSearchFields={
											scanlogsSearchFields
										}
									/>
								),
							},
							sx: {
								'& [aria-label="search"]': {
									display: 'none',
								},
								'& [aria-label="clear"]': {
									position: 'relative',
									zIndex: 2,
								},
							},
						}}
						showSearchFields={showSearchFields}
						state={{
							isLoading,
							columnVisibility,
							sorting,
						}}
						initialState={{
							expanded: true,
						}}
						isColumnVisibilityDrawerOpen={
							isColumnVisibilityDrawerOpen
						}
						onCloseColumnVisibilityDrawer={() => {
							setIsColumnVisibilityDrawerOpen(false);
							setSideMenuSearchValue('');
						}}
						onClickColumnVisibilityButton={() =>
							setIsColumnVisibilityDrawerOpen(true)
						}
						filterChips={generateFilterChips(
							filters,
							handleRemoveColumnFilter,
						)}
						filterChipsClearLabel="Clear all"
						onFilterChipsClearAll={handleClearAllFilters}
						enableTopToolbar={enableTopToolbar}
						topBarActionItems={[
							<TopBarActions
								key="top-bar-actions"
								isLoading={isLoading}
								onDownload={downloadFile}
								onRefresh={handleRefresh}
							/>,
						]}
						enableFilters={enableFilters}
						enableGrouping={enableGrouping}
						enableColumnDragging={enableColumnDragging}
						enableColumnFilterModes={enableColumnFilterModes}
						manualSorting={manualSorting}
						onSortingChange={setSorting}
						enablePagination={enablePagination}
						enableStickyHeader={enableStickyHeader}
						enableColumnFilters={enableColumnFilters}
						enableGlobalFilter={enableGlobalFilter}
						muiTableBodyCellProps={{
							sx: {
								padding: '12px',
							},
						}}
						muiTableHeadCellProps={{
							sx: {
								padding: '12px !important',
							},
						}}
						muiTableContainerProps={{
							sx: {
								marginTop: '20px',
								overflowY: 'auto',
								maxHeight: '450px',
							},
						}}
						paginationProps={{
							page: page,
							totalItems: totalRecords,
							itemsPerPage: pageSize,
							itemsPerPageOptions: [10, 25, 50, 100],
							itemsPerPageDescription: 'items per page',
							onChange: handlePaginationChange,
							onClickItemsPerPageOption:
								handleOnClickItemsPerPageOption,
						}}
						enableBottomToolbar={totalRecords > 0}
						sideMenuProps={{
							dropdowns: sideMenuDropdowns,
							onChange: (e) =>
								setSideMenuSearchValue(e.target.value.trim()),
							renderDropdownItemsEmptyState: ():
								| React.ReactNode
								| React.ReactNode[] => <div />,
							subtitle: 'Select columns to add',
							title: 'Table columns',
							scrollableItemContainer: true,
						}}
						sideMenuOnCheckboxChange={handleSideMenuCheckboxChange}
						columnButtonBeforeActions={true}
					/>
				</LocalizationProvider>
			</Grid>
		</BDSProvider>
	);
};
